import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthService from "services/auth.service";
import { Field, Button, FormSection } from "components/ui";

const IconUser = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const IconMail = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
const IconLock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconPhone = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
const IconCard = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const Register = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", password: "",
    phone: "", cin: "", age: "",
  });
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = { ...form, age: form.age ? parseInt(form.age, 10) : null };
    AuthService.register(payload).then(
      () => { navigate("/auth/login"); },
      (error) => {
        const msg =
          (error.response && error.response.data && error.response.data.message) ||
          error.message || error.toString();
        setMessage(msg);
        setSubmitting(false);
      }
    );
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1.05fr",
        background: "rgba(255,255,255,0.03)",
        backdropFilter: "blur(18px)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 24,
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
        overflow: "hidden",
        minHeight: 580,
      }}
      className="auth-split"
    >
      {/* Form */}
      <section style={{ padding: "48px", background: "#fff", color: "var(--text-body)" }}>
        <div style={{ maxWidth: 520, marginInline: "auto" }}>
          <span className="t-body-xs" style={{ color: "var(--brand-primary)" }}>Create account</span>
          <h2 className="t-heading-lg" style={{ marginTop: 6, marginBottom: 6 }}>Set up your StockPos workspace</h2>
          <p className="t-body-sm t-muted" style={{ marginBottom: 24 }}>
            We just need a few details to get you started.
          </p>

          {message && (
            <div
              role="alert"
              style={{
                background: "var(--danger-bg)", color: "#991B1B",
                border: "1px solid var(--danger-border)", borderRadius: 10,
                padding: "10px 14px", fontSize: 13, marginBottom: 16,
              }}
            >
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: "grid", gap: 22 }}>
            <FormSection title="Personal" columns={2}>
              <Field label="First name" required value={form.firstName} onChange={change("firstName")} icon={<IconUser />} placeholder="Jane" />
              <Field label="Last name"  required value={form.lastName}  onChange={change("lastName")}  icon={<IconUser />} placeholder="Doe" />
            </FormSection>

            <FormSection title="Contact" columns={2}>
              <Field label="Email" type="email" required value={form.email} onChange={change("email")} icon={<IconMail />} placeholder="you@company.com" autoComplete="email" />
              <Field label="Phone" type="tel"   required value={form.phone} onChange={change("phone")} icon={<IconPhone />} placeholder="+216 …" />
            </FormSection>

            <FormSection title="Identity" columns={2}>
              <Field label="CIN" required value={form.cin} onChange={change("cin")} icon={<IconCard />} placeholder="National ID" />
              <Field label="Age" type="number" required value={form.age} onChange={change("age")} placeholder="25" />
            </FormSection>

            <FormSection title="Security">
              <Field label="Password" type="password" required value={form.password} onChange={change("password")} icon={<IconLock />} placeholder="At least 8 characters" autoComplete="new-password" />
            </FormSection>

            <Button type="submit" variant="primary" block loading={submitting}>
              Create account
            </Button>
          </form>

          <p style={{ marginTop: 20, fontSize: 13, color: "var(--text-muted)", textAlign: "center" }}>
            Already have an account?{" "}
            <Link to="/auth/login" style={{ color: "var(--brand-primary)", fontWeight: 600, textDecoration: "none" }}>
              Sign in
            </Link>
          </p>
        </div>
      </section>

      {/* Brand panel */}
      <aside
        style={{
          padding: "56px 48px",
          display: "flex",
          flexDirection: "column",
          gap: 24,
          background:
            "radial-gradient(120% 80% at 100% 0%, rgba(79,70,229,0.45) 0%, transparent 55%), radial-gradient(120% 80% at 0% 100%, rgba(127,119,221,0.35) 0%, transparent 60%)",
          color: "#fff",
        }}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 44, height: 44, borderRadius: 12,
              background: "linear-gradient(135deg, #4F46E5, #7F77DD)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, color: "#fff",
              boxShadow: "0 10px 30px rgba(79,70,229,0.55)",
            }}
          >
            S
          </span>
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, letterSpacing: "-0.02em" }}>
            STOCK<span style={{ color: "#7F77DD" }}>POS</span>
          </span>
        </div>

        <h1 className="t-display-lg" style={{ color: "#fff", margin: 0, maxWidth: 460 }}>
          Stock, sales, and books — finally one product.
        </h1>
        <p style={{ color: "rgba(255,255,255,0.72)", fontSize: 15, lineHeight: 1.6, maxWidth: 460 }}>
          Join hundreds of retailers running multi-branch operations with confidence. You'll be set up in under five minutes.
        </p>

        <div
          style={{
            marginTop: "auto",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.10)",
            borderRadius: 16,
            padding: 20,
          }}
        >
          <p style={{ color: "rgba(255,255,255,0.92)", fontStyle: "italic", margin: 0, fontSize: 14, lineHeight: 1.6 }}>
            "We replaced three tools with StockPos and closed our books a week early last quarter."
          </p>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, marginTop: 10, marginBottom: 0 }}>
            — Operations lead, multi-branch retail
          </p>
        </div>
      </aside>

      <style>{`
        @media (max-width: 900px) {
          .auth-split { grid-template-columns: 1fr !important; min-height: auto !important; }
          .auth-split > aside { padding: 32px 28px !important; order: -1; }
          .auth-split > section { padding: 36px 28px !important; }
        }
      `}</style>
    </div>
  );
};

export default Register;
